import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { InitiativeTabsSection } from '../SharedInitiativeTabs';
import '../Growth/growthForm.scss';

const buildInitialFilters = (data = {}, mode = 'edit') => {
  const now = new Date();
  const createdOn =
    data.created_on ||
    now.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  return {
    kpi: data.project_objective || data.kpi || '',
    project_id: data.project_id || '',
    project_description: data.project_description || '',
    report_to: data.report_to || '',
    user_name: data.user_name || data.account_owner || '',
    created_on: createdOn,
    enabling_one_description: data.enabling_one_description || '',
    enabling_two_description: data.enabling_two_description || '',
  };
};

const InterventionsForm = ({
  mode = 'edit',
  initialData = {},
  onBack,
  onSave,
}) => {
  const seed = useMemo(() => buildInitialFilters(initialData, mode), [initialData, mode]);
  const [filters, setFilters] = useState(seed);
  const [locked, setLocked] = useState(Boolean(initialData.is_locked));
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    setFilters(buildInitialFilters(initialData, mode));
    setLocked(Boolean(initialData.is_locked));
  }, [initialData, mode]);

  const updateField = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveDraft = () => {
    onSave({
      ...initialData,
      ...filters,
      project_objective: filters.kpi,
      is_locked: locked,
    });
  };

  const handleRevert = () => {
    setFilters(buildInitialFilters(initialData, mode));
    setCancelOpen(false);
  };

  return (
    <div className="growth-form-page">
      <Box className="back-row">
        <Button variant="outlined" size="small" className="back-btn" onClick={onBack}>
          Back
        </Button>
      </Box>

      <Box className="form-card">
        <Box className="form-grid">
          <Box className="form-col">
            <Box className="two-col">
              <Box display="flex" flexDirection="column" gap={1.75}>
                <TextField
                  size="small"
                  fullWidth
                  label="Project Objectives"
                  value={filters.kpi}
                  onChange={(e) => updateField('kpi', e.target.value)}
                  disabled={locked}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Project ID"
                  value={filters.project_id}
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Project Description"
                  value={filters.project_description}
                  onChange={(e) => updateField('project_description', e.target.value)}
                  disabled={locked}
                />
              </Box>

              <Box display="flex" flexDirection="column" gap={1.75}>
                <TextField
                  size="small"
                  fullWidth
                  label="Report to"
                  value={filters.report_to}
                  onChange={(e) => updateField('report_to', e.target.value)}
                  disabled={locked}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Created On"
                  value={filters.created_on}
                  disabled
                />
              </Box>
            </Box>
          </Box>

          <Box className="form-col">
            <Box className="enabling-row" sx={{ marginTop: 0 }}>
              <Box>
                <div className="enabling-label">Enabling Initiative 1</div>
                <TextField
                  label="Initiative Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={filters.enabling_one_description}
                  onChange={(e) => updateField('enabling_one_description', e.target.value)}
                  disabled={locked}
                />
              </Box>
              <Box>
                <div className="enabling-label">Enabling Initiative 2</div>
                <TextField
                  label="Initiative Description"
                  fullWidth
                  multiline
                  rows={4}
                  value={filters.enabling_two_description}
                  onChange={(e) => updateField('enabling_two_description', e.target.value)}
                  disabled={locked}
                />
              </Box>
            </Box>

            <Box className="actions-row">
              <Button
                className="btn-lock"
                variant="outlined"
                color={locked ? 'error' : 'primary'}
                onClick={() => setLocked((v) => !v)}
                disabled={mode === 'create'}
                startIcon={locked ? <LockOpenIcon fontSize="small" /> : <LockIcon fontSize="small" />}
              >
                {locked ? 'Unlock' : 'Lock'}
              </Button>
              <Button
                className="btn-save"
                variant="contained"
                onClick={handleSaveDraft}
                disabled={locked}
              >
                Save Draft
              </Button>
              <Button
                className="btn-cancel"
                variant="contained"
                color="error"
                onClick={() => setCancelOpen(true)}
                disabled={locked || mode === 'create'}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <InitiativeTabsSection />

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)}>
        <DialogTitle>Confirm Cancel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revert changes? All unsaved edits will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} color="primary">
            No
          </Button>
          <Button onClick={handleRevert} color="error" variant="contained">
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InterventionsForm;
